import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Mic, Lightbulb, Network, Check, ChevronRight, Users, Heart, Award, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { conferenceHighlights } from '../data/mock';

const SAVE_THE_DATE_IMAGE = 'https://customer-assets-cm19k8pv.emergentagent.net/job_bed60f41-c9a7-4b54-a8f8-ba2780b910d1/artifacts/4azq1rpd_DC%20Men%20in%20ECE%20Conference%20Flyer.png';
const SPONSORSHIP_FLYER_PDF = 'https://customer-assets-cm19k8pv.emergentagent.net/job_bed60f41-c9a7-4b54-a8f8-ba2780b910d1/artifacts/vn9n5vl0_2026%20Sponsorship%20Call_Flyer.pdf';
const DONATION_URL = 'https://www.zeffy.com/en-US/donation-form/dc-men-in-early-childhood-education';

const sponsorshipTiers = [
  {
    name: 'Legacy',
    amount: '$15,000+',
    icon: Award,
    featured: true,
    accent: 'from-brand-gold to-gold-600',
    benefits: [
      'Logo on Conference Website',
      'Logo in Program',
      'Main Stage Recognition',
      'Multiple Social Media Recognitions',
      'Premium Exhibitor Table',
      'Speaking Opportunity',
      'VIP Reserved Seating',
      'Post-Conference Recognition',
    ],
  },
  {
    name: 'Visionary',
    amount: '$5,001 – $15,000',
    icon: Star,
    featured: false,
    accent: 'from-brand-green to-green-600',
    benefits: [
      'Logo on Conference Website',
      'Logo in Program',
      'Main Stage Recognition',
      'Featured Social Media Recognition',
      'Standard Exhibitor Table',
      'VIP Reserved Seating',
      'Post-Conference Recognition',
    ],
  },
  {
    name: 'Community',
    amount: 'Up to $5,000',
    icon: Users,
    featured: false,
    accent: 'from-brand-navy to-navy-700',
    benefits: [
      'Logo on Conference Website',
      'Logo in Program',
      'Social Media Recognition',
      'Standard Exhibitor Table',
      'Post-Conference Recognition',
    ],
  },
];

const exclusiveOpportunities = [
  'Breakfast', 'Lunch', 'Networking Reception', 'Registration',
  'Conference Bags', 'Lanyards', 'Coffee Break', 'Speakers + more!',
];

const iconMap = {
  Mic: Mic,
  Lightbulb: Lightbulb,
  Network: Network,
};

const highlightColors = [
  { bg: 'from-brand-gold to-gold-600', shadow: 'shadow-gold-500/20', text: 'text-navy-900' },
  { bg: 'from-brand-green to-green-600', shadow: 'shadow-green-500/20', text: 'text-white' },
  { bg: 'from-brand-red to-red-600', shadow: 'shadow-red-500/20', text: 'text-white' },
];

export default function ConferencePage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-navy-900 via-navy-800 to-brand-navy">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-64 h-64 bg-brand-green/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-navy-400 mb-6">
              <Link to="/" className="hover:text-brand-gold transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white font-medium">Conference</span>
            </nav>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6">
              <Calendar className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-medium text-brand-gold">Annual Event</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              DC Men in ECE 2026 Annual{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold to-brand-green">
                City-Wide Conference
              </span>
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed mb-8">
              Transforming education together at the DC Men in Early Childhood Education Conference! Discover the incredible impact you can have in the realm of early childhood education.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green/10 border border-brand-green/20">
                <Check className="w-4 h-4 text-brand-green" />
                <span className="text-sm text-green-300">Free & Open to the Public</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20">
                <Users className="w-4 h-4 text-brand-gold" />
                <span className="text-sm text-gold-300">All Genders Welcome</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Video Section */}
      <section className="py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Watch Our Conference Promos</h2>
            <p className="text-navy-300">Get a glimpse of what awaits you at the DC Men in ECE Annual Conference.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/30" data-testid="conference-promo-video-1">
              <video
                controls
                preload="metadata"
                className="w-full h-auto block bg-black"
                poster="/images/conference-registration.jpg"
              >
                <source src="/images/conference-promo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/30" data-testid="conference-promo-video-2">
              <video
                controls
                preload="metadata"
                className="w-full h-auto block bg-black"
              >
                <source
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_bed60f41-c9a7-4b54-a8f8-ba2780b910d1/artifacts/b7y6esmb_DC%20Men%20in%20ECE%20Promo%20Vid%201%20%281%29.mp4"
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Why Attend */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">Why You Should Attend</h2>
            <p className="text-navy-600">This conference is your gateway to innovative strategies, inspiring stories, and powerful networking.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {conferenceHighlights.map((highlight, index) => {
              const Icon = iconMap[highlight.icon] || Mic;
              const colors = highlightColors[index];
              return (
                <Card key={index} className="group border-0 shadow-lg shadow-navy-100 hover:shadow-xl hover:shadow-navy-100/50 transition-all duration-500">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg ${colors.shadow}`}>
                      <Icon className={`w-7 h-7 ${colors.text}`} />
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3">{highlight.title}</h3>
                    <p className="text-navy-600 leading-relaxed">{highlight.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conference Details */}
      <section className="py-24 bg-gradient-to-br from-brand-green to-green-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Conference Details</h2>
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h4 className="font-semibold text-white mb-2">Who Should Attend?</h4>
                  <p className="text-green-100">This premier event is designed by DC Men in ECE and is open to all regardless of gender. Anyone interested in early childhood education is welcome.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h4 className="font-semibold text-white mb-2">What to Expect</h4>
                  <p className="text-green-100">Interactive workshops, inspiring keynote speakers, networking sessions, and practical strategies you can implement immediately.</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h4 className="font-semibold text-white mb-2">Registration</h4>
                  <p className="text-green-100">Spaces are limited for this unique gathering of dedicated professionals. Don&apos;t miss out on this empowering event!</p>
                </div>
              </div>
            </div>
            
            {/* Conference Image */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-4 border border-white/20 overflow-hidden">
              <img 
                src="/images/conference-registration.jpg" 
                alt="DC Men in ECE Conference Registration"
                className="w-full h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Save the Date — 2026 Conference */}
      <section className="py-24 bg-gradient-to-br from-navy-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6">
              <Calendar className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-medium text-brand-gold" data-testid="save-the-date-badge">Save the Date</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              4th Annual Citywide Conference
            </h2>
            <p className="text-lg text-navy-600 max-w-2xl mx-auto">
              <strong>October 12, 2026</strong> &nbsp;•&nbsp; Aspen Institute, Washington, DC NW
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-brand-navy">
            <img
              src={SAVE_THE_DATE_IMAGE}
              alt="Register Today — DC Men in ECE 4th Annual Citywide Conference, Monday October 12, 2026, The Aspen Institute, Washington DC NW. Brotherhood, Belonging, and Brilliance: Redefining Men in ECE. Keynote, Workshops, Panels, Networking."
              className="w-full h-auto block"
              loading="lazy"
              data-testid="save-the-date-flyer"
            />
          </div>

          <div className="text-center mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/conference/register">
              <Button
                size="lg"
                className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-bold px-8 shadow-xl shadow-gold-500/30"
                data-testid="save-the-date-register-btn"
              >
                Register Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a
              href={SAVE_THE_DATE_IMAGE}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="save-the-date-download"
            >
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50"
              >
                Download Flyer
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Sponsorship Levels */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-testid="sponsorship-section">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-green/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6">
              <Heart className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-medium text-brand-gold">Sponsor the 2026 Conference</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Invest in Leadership. Champion Equity.
            </h2>
            <p className="text-navy-300 leading-relaxed">
              Your sponsorship directly supports professional learning opportunities, workforce development
              initiatives, equity in early childhood education, and networking and leadership development.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-14">
            {sponsorshipTiers.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.name}
                  data-testid={`sponsorship-tier-${tier.name.toLowerCase()}`}
                  className={`relative rounded-3xl p-8 flex flex-col transition-transform duration-300 hover:-translate-y-2
                    ${tier.featured
                      ? 'bg-white shadow-2xl shadow-gold-500/20 border-2 border-brand-gold'
                      : 'bg-white/5 backdrop-blur-sm border border-white/10'}`}
                >
                  {tier.featured && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-brand-gold text-navy-900 text-xs font-bold uppercase tracking-wider">
                      Top Tier
                    </span>
                  )}
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tier.accent} flex items-center justify-center mb-6 shadow-lg`}>
                    <Icon className={`w-7 h-7 ${tier.featured ? 'text-navy-900' : 'text-white'}`} />
                  </div>
                  <h3 className={`text-2xl font-bold mb-1 ${tier.featured ? 'text-navy-900' : 'text-white'}`}>{tier.name}</h3>
                  <p className={`text-lg font-semibold mb-6 ${tier.featured ? 'text-brand-green' : 'text-brand-gold'}`}>{tier.amount}</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 shrink-0 mt-0.5 ${tier.featured ? 'text-brand-green' : 'text-brand-gold'}`} />
                        <span className={`text-sm ${tier.featured ? 'text-navy-700' : 'text-navy-200'}`}>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-donate-btn-${tier.name.toLowerCase()}`}>
                    <Button
                      className={`w-full font-bold ${tier.featured
                        ? 'bg-brand-gold hover:bg-gold-600 text-navy-900 shadow-lg shadow-gold-500/30'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'}`}
                    >
                      Become a {tier.name} Sponsor
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              );
            })}
          </div>

          {/* Exclusive Sponsorship Opportunities */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 sm:p-10 mb-12" data-testid="exclusive-sponsorships">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-brand-gold" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Exclusive Sponsorship Opportunities</h3>
            </div>
            <p className="text-navy-300 mb-6">Support a signature experience during the conference:</p>
            <div className="flex flex-wrap gap-3">
              {exclusiveOpportunities.map((item) => (
                <span key={item} className="px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 text-sm font-medium text-gold-300">
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Donate CTA + Tax Note */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a href={DONATION_URL} target="_blank" rel="noopener noreferrer" data-testid="sponsorship-donate-btn">
                <Button size="lg" className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-bold px-10 shadow-xl shadow-gold-500/30">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate / Sponsor Now
                </Button>
              </a>
              <a href={SPONSORSHIP_FLYER_PDF} target="_blank" rel="noopener noreferrer" data-testid="sponsorship-flyer-download">
                <Button size="lg" variant="outline" className="border-2 border-white/30 bg-transparent text-white font-semibold px-8 hover:bg-white/10 hover:text-white">
                  Download Sponsorship Flyer
                </Button>
              </a>
            </div>
            <p className="text-sm text-navy-400 max-w-2xl mx-auto" data-testid="tax-deductible-note">
              Contributions are tax-deductible through our fiscal sponsor, the D.C. Early Learning Collaborative,
              to the extent provided by law.
            </p>
          </div>
        </div>
      </section>

      {/* Register CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Registration Open Banner */}
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-brand-green/20 to-brand-gold/20 border border-brand-green/30 mb-8">
            <Calendar className="w-5 h-5 text-brand-green" />
            <span className="text-lg font-bold text-navy-900">Conference Registration</span>
            <span
              className="px-3 py-1 rounded-full bg-brand-green text-white text-sm font-bold"
              data-testid="registration-open-badge"
            >
              NOW OPEN
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
            Registration Is Open — Reserve Your Spot
          </h2>
          <p className="text-lg text-navy-600 mb-10">
            The 4th Annual Citywide Conference — <strong>October 12, 2026</strong> at the Aspen Institute in Washington, DC.
            Registration is <strong>free</strong>. Complete the six-step form to secure your seat and get your confirmation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/conference/register">
              <Button
                size="lg"
                className="bg-brand-gold hover:bg-gold-600 text-navy-900 font-semibold px-10 shadow-xl shadow-gold-500/30"
                data-testid="conference-register-now-btn"
              >
                Register Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/join">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-navy-200 text-navy-700 font-semibold px-8 hover:bg-navy-50"
              >
                Become a Member
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
